import {Controller} from "stimulus";
import {Liquid} from "liquidjs";

/**
 * Add this controller to a form, and add templates to targets to have them
 * rendered when the form updates.
 */
export default class TemplatedFieldsController extends Controller {
  static targets = ["templatedField"];

  static values = {
    prefix: String,
  };

  declare prefixValue: string;
  declare templatedFieldTargets: HTMLElement[];

  get form(): HTMLFormElement {
    return this.element.tagName == "FORM"
      ? this.element as HTMLFormElement
      : this.element.closest("form")!;
  }

  connect(): void {
    this.render();
  }

  render(): void {
    this.templatedFieldTargets.forEach((field) => {
      this.renderFieldValue(this.getInput(field), this.getExpression(field));
    });
  }

  getInput(elem: HTMLElement): HTMLInputElement {
    return elem.getElementsByTagName("input")[0];
  }

  getExpression(elem: HTMLElement): string {
    return elem.dataset.templatedFieldsExpression || "";
  }

  renderFieldValue(field: HTMLInputElement, expression: string): void {
    if (!expression) return;

    const formData: Record<string, any> = this.buildFormObject();
    const parserContext = this.prefixValue ? formData[this.prefixValue] || {} : formData;

    try {
      const result = new Liquid().parseAndRenderSync(expression, {data: parserContext});
      field.value = result;
    } catch(error) {
      console.error(`Error evaluating expression "${expression}":`, error);
    }
  }

  /*
   * Builds an object graph based on form controls' names.
   *
   * @example
   *   For form controls with names like:
   *   - user[name]
   *   - user[address][street]
   *   - order[items][0][price]
   *
   *    The resulting object will be:
   *    {
   *      user: {
   *        name: ...,
   *        address: {
   *          street: ...
   *        }
   *      },
   *      order: {
   *        items: [
   *          { price: ... }
   *        ]
   *      }
   *    }
   */
  buildFormObject(): Record<string, any> {
    const formObject: Record<string, any> = {};
    const formValues = this.getFormValues();
    for (const [name, value] of Object.entries(formValues)) {
      const nameParts = name.split("[").map(part => part.replace("]", ""));
      let currentLevel = formObject;

      nameParts.forEach((part, index) => {
        if (index === nameParts.length - 1) {
          currentLevel[part] = value;
        } else {
          if (!currentLevel[part]) {
            currentLevel[part] = {};
          }
          currentLevel = currentLevel[part];
        }
      });
    };

    return formObject;
  }

  /*
    Gets the form values with their names as keys.

    Drops any values that aren't part of the user facing form, e.g.
    authenticity_token or other hidden Rails fields.

    Does some simple casting to ensure checkboxes can be used as booleans in
    templates.
   */
  getFormValues(): Record<string, any> {
    const formData = new FormData(this.form);
    const formValues = new Object();

    const isCheckbox = (name: string): boolean => {
      const elem = this.form.elements.namedItem(name);
      return elem instanceof RadioNodeList && Array.from(elem.values()).some(v => (v as HTMLInputElement).type === "checkbox");
    };

    const checkboxDataType = (name: string): string | undefined => {
      const elem = this.form.elements.namedItem(name);
      if (!(elem instanceof RadioNodeList)) return undefined;
      const checkbox = Array.from(elem.values()).find(v => (v as HTMLInputElement).type === "checkbox") as HTMLInputElement | undefined;
      return checkbox?.closest<HTMLElement>("[data-type]")?.dataset.type || "boolean";
    };

    for (const [key, value] of formData.entries()) {
      if (key.startsWith(this.prefixValue)) {
        if (isCheckbox(key) && checkboxDataType(key) === "boolean") {
          formValues[key] = (value === "true") || (value === "1");
        } else {
          formValues[key] = value;
        }
      }
    }

    // disabled inputs aren't included in FormData, so we need to add them manually
    this.templatedFieldTargets.forEach((field) => {
      const input = this.getInput(field);
      formValues[input.name] = input.value;
    });

    return formValues;
  }
}
